/**
 * ProjectBoardFilters Component
 *
 * Modern filter interface following 2025 design patterns:
 * - Collapsible filter panel
 * - Active filter chips for quick overview
 * - Compact filter selects with integrated labels
 * - Progressive disclosure for complex filters
 *
 * Design patterns applied:
 * - Chip filters for active state display
 * - Accordion/collapsible for filter groups
 * - Context-aware filters (show relevant options)
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Select,
  Button,
  Dropdown,
  Menu,
  DatePicker,
} from 'antd';
import {
  CloseOutlined,
  DownOutlined,
  SaveOutlined,
  CloudDownloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import moment from 'moment';
import { projectStatusOptions } from '../../configs/project.config';
import '../../styles/index.scss';

const { Option } = Select;

const ProjectBoardFilters = ({
  windowWidth,
  showFilters,
  setShowFilters,
  searchParams,
  // Filter handlers
  handleTeamMembersFilter,
  handleOwnerFilter,
  handleTimelineFilter,
  handleStatusFilter,
  // User data
  teamMemberUsers,
  ownerUsers,
  // Loading states
  teamMemberUsersLoading,
  ownerUsersLoading,
  // Fetch functions
  handleFetchTeamMemberUsers,
  handleFetchOwnerUsers,
  // Filter actions
  onClearFilters,
  onSaveFilters,
  onLoadFilters,
}) => {
  const isMobile = windowWidth < 768;

  // Calculate active filter count and chips
  const activeFilters = useMemo(() => {
    const filters = [];

    const formatUserNames = (ids, users) => {
      const names = ids
        .map((id) => {
          const user = users.find((u) => u.id === id);
          return user ? startCase(user.name || user.username) : null;
        })
        .filter(Boolean);
      return names.length ? names.join(', ') : `${ids.length} selected`;
    };

    if (searchParams.teamMembers) {
      const members = searchParams.teamMembers.split(',');
      filters.push({
        key: 'teamMembers',
        label: `Team: ${formatUserNames(members, teamMemberUsers)}`,
        category: 'team',
        onRemove: () => handleTeamMembersFilter([]),
      });
    }

    if (searchParams.owner) {
      const owners = searchParams.owner.split(',');
      filters.push({
        key: 'owner',
        label: `Owner: ${formatUserNames(owners, ownerUsers)}`,
        category: 'team',
        onRemove: () => handleOwnerFilter([]),
      });
    }

    if (searchParams.startDate && searchParams.endDate) {
      filters.push({
        key: 'timeline',
        label: 'Timeline',
        category: 'date',
        onRemove: () => handleTimelineFilter(null),
      });
    }

    if (searchParams.status) {
      const statuses = searchParams.status.split(',');
      const statusLabels = statuses
        .map((value) => projectStatusOptions.find((s) => s.value === value)?.label)
        .filter(Boolean);
      filters.push({
        key: 'status',
        label: `Status: ${statusLabels.length ? statusLabels.join(', ') : `${statuses.length} selected`}`,
        category: 'status',
        onRemove: () => handleStatusFilter([]),
      });
    }

    return filters;
  }, [
    searchParams,
    teamMemberUsers,
    ownerUsers,
    handleTeamMembersFilter,
    handleOwnerFilter,
    handleTimelineFilter,
    handleStatusFilter,
  ]);

  // Filter presets menu
  const filterPresetsMenu = (
    <Menu>
      <Menu.Item key="save" icon={<SaveOutlined />} onClick={onSaveFilters}>
        Save Current Filters
      </Menu.Item>
      <Menu.Item key="load" icon={<CloudDownloadOutlined />} onClick={onLoadFilters}>
        Load Saved Filters
      </Menu.Item>
    </Menu>
  );

  // Render compact filter select
  const renderFilterSelect = ({
    placeholder,
    value,
    onChange,
    options,
    loading,
    onSearch,
    showSearch = false,
    mode = 'multiple',
  }) => (
    <Select
      className="project-filter-select"
      dropdownClassName="project-filter-dropdown"
      mode={mode}
      showSearch={showSearch}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={loading}
      onSearch={onSearch}
      optionLabelProp="label"
      allowClear
      filterOption={(input, option) => {
        if (typeof option?.label === 'string') return option.label.toLowerCase().includes(input.toLowerCase());
        if (typeof option?.children === 'string') return option.children.toLowerCase().includes(input.toLowerCase());
        return false;
      }}
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 200 }}
    >
      {options}
    </Select>
  );

  if (!showFilters) {
    // Mobile: Active Filters Summary
    if (isMobile && activeFilters.length > 0) {
      return (
        <div className="project-filters__mobile-summary">
          <span>{activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} active</span>
          <Button type="link" size="small" onClick={onClearFilters}>
            Clear all
          </Button>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="project-board-filters-modern">
      {/* Filter Panel Header */}
      <div className="project-filters__panel-header">
        <span className="project-filters__panel-title">Filters</span>
        <div className="project-filters__panel-actions">
          <Dropdown overlay={filterPresetsMenu} trigger={['click']}>
            <Button className="project-filters__presets" size="small">
              {isMobile ? <SaveOutlined /> : 'Presets'}
              <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            className="project-filters__close"
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setShowFilters(false)}
            size="small"
          />
        </div>
      </div>

      {/* Filter Dropdowns */}
      <div className="project-filters__panel">
        <Row gutter={[12, 12]} className="project-filters__row">
          {/* Team Members Filter */}
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            {renderFilterSelect({
              placeholder: 'Team Members',
              value: searchParams.teamMembers ? searchParams.teamMembers.split(',') : [],
              onChange: handleTeamMembersFilter,
              loading: teamMemberUsersLoading,
              onSearch: handleFetchTeamMemberUsers,
              showSearch: true,
              options: teamMemberUsers.map((user) => (
                <Option key={user.id} value={user.id} label={startCase(user.name || user.username)}>
                  {startCase(user.name || user.username)}
                </Option>
              )),
            })}
          </Col>

          {/* Owner Filter */}
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            {renderFilterSelect({
              placeholder: 'Owner',
              value: searchParams.owner ? searchParams.owner.split(',') : [],
              onChange: handleOwnerFilter,
              loading: ownerUsersLoading,
              onSearch: handleFetchOwnerUsers,
              showSearch: true,
              options: ownerUsers.map((user) => (
                <Option key={user.id} value={user.id} label={startCase(user.name || user.username)}>
                  {startCase(user.name || user.username)}
                </Option>
              )),
            })}
          </Col>

          {/* Timeline Filter */}
          <Col xs={24} sm={12} md={6} lg={4} xl={3}>
            <DatePicker.RangePicker
              className="project-filter-datepicker"
              placeholder={isMobile ? ['Start', 'End'] : ['Start Date', 'End Date']}
              value={
                searchParams.startDate && searchParams.endDate
                  ? [
                      moment(searchParams.startDate, 'YYYY-MM-DD'),
                      moment(searchParams.endDate, 'YYYY-MM-DD'),
                    ]
                  : []
              }
              onChange={handleTimelineFilter}
              allowClear
              format="MMM D"
              suffixIcon={<CalendarOutlined />}
              popupStyle={{
                maxWidth: isMobile ? '90vw' : 'auto',
                left: isMobile ? '50%' : undefined,
                transform: isMobile ? 'translateX(-50%)' : undefined,
              }}
              dropdownClassName={isMobile ? 'task-mobile-range-picker-dropdown' : ''}
              getPopupContainer={isMobile ? (trigger) => trigger.parentElement : undefined}
            />
          </Col>

          {/* Status Filter */}
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            {renderFilterSelect({
              placeholder: 'Status',
              value: searchParams.status ? searchParams.status.split(',') : [],
              onChange: handleStatusFilter,
              options: projectStatusOptions.map((status) => (
                <Option key={status.value} value={status.value} label={status.label}>
                  {status.label}
                </Option>
              )),
            })}
          </Col>
        </Row>
      </div>
    </div>
  );
};

ProjectBoardFilters.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  // Filter handlers
  handleTeamMembersFilter: PropTypes.func.isRequired,
  handleOwnerFilter: PropTypes.func.isRequired,
  handleTimelineFilter: PropTypes.func.isRequired,
  handleStatusFilter: PropTypes.func.isRequired,
  // User data
  teamMemberUsers: PropTypes.array.isRequired,
  ownerUsers: PropTypes.array.isRequired,
  // Loading states
  teamMemberUsersLoading: PropTypes.bool,
  ownerUsersLoading: PropTypes.bool,
  // Fetch functions
  handleFetchTeamMemberUsers: PropTypes.func.isRequired,
  handleFetchOwnerUsers: PropTypes.func.isRequired,
  // Filter actions
  onClearFilters: PropTypes.func.isRequired,
  onSaveFilters: PropTypes.func.isRequired,
  onLoadFilters: PropTypes.func.isRequired,
};

ProjectBoardFilters.defaultProps = {
  teamMemberUsersLoading: false,
  ownerUsersLoading: false,
};

export default ProjectBoardFilters;
